import {Component, OnDestroy, OnInit} from '@angular/core';
import {DatePipe, NgForOf, NgIf} from '@angular/common';
import {Room} from '../../../models/room';
import {Region} from '../../../models/region';
import {RoomBoxComponent} from '../room-box/room-box.component';
import {RoomListSearchComponent} from './room-list-search/room-list-search.component';
import {RoomListFilterComponent} from './room-list-filter/room-list-filter.component';
import {RoomListEmptyComponent} from './room-list-empty/room-list-empty.component';
import {RoomListDetailComponent} from './room-list-detail/room-list-detail.component';
import {combineLatest, finalize, firstValueFrom, forkJoin, from, map, Subject, takeUntil} from 'rxjs';
import {ActivatedRoute} from '@angular/router';
import {RegionService} from '../../../services/region-service';
import {AuthService} from '../../../services/auth.service';
import {FilterService} from '../../../services/filter.service';
import {MapMark, MapService} from '../../../services/map.service';
import {GeneralService} from '../../../services/general.service';
import {RoomService} from '../../../services/room-service';
import {ScheduleService} from '../../../services/schedule.service';
import {PrettyTimePipe} from '../../pipes/pretty-time.pipe';
import {Place} from '../../../models/place';
import {PlaceService} from '../../../services/place.service';

@Component({
  selector: 'app-room-list',
  imports: [
    NgForOf,
    NgIf,
    RoomBoxComponent,
    RoomListSearchComponent,
    RoomListFilterComponent,
    RoomListEmptyComponent,
    RoomListDetailComponent,
    DatePipe,
    PrettyTimePipe
  ],
  templateUrl: './room-list.component.html',
  styleUrl: './room-list.component.scss'
})
export class RoomListComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject<boolean>();

  // Region
  isRegionLoading = false
  isRegionLoaded = false
  regionSlug: string | null = null
  region: Region | null = null

  // Neighborhood
  neighborhoodSlug: string | null = null

  // Place
  isPlaceLoaded = false
  isPlaceLoading = false
  placeSlug: string | null = null
  place: Place | null = null

  // Rooms
  isRoomsLoading = false
  isRoomsLoaded = false
  rooms: Room[] = []

  // Filters
  isFilterGeolocation = false
  isFilterReservation = false

  // Geolocation filter
  latitude: number | null = null
  longitude: number | null = null
  zoom: number | null = null

  // Reservation filter
  reserve: string | null = null
  reserveDuration: number | null = null
  reservePeople: number | null = null

  // Loading
  loading = false

  constructor(private route: ActivatedRoute,
              private roomService: RoomService,
              private regionService: RegionService,
              private placeService: PlaceService,
              private filterService: FilterService,
              private mapService: MapService,
              private scheduleService: ScheduleService,
              private generalService: GeneralService) {
  }

  ngOnInit() {

    // Listen to route and query parameter change
    this.loading = true
    combineLatest([this.route.params, this.route.queryParams]).pipe(
      takeUntil(this.onDestroy$),
      map(results => ({params: results[0], queryParams: results[1]})))
      .subscribe(async results => {

        // Filter search
        const latitude = results.queryParams['latitude']
        const longitude = results.queryParams['longitude']
        const reserve = results.queryParams['reserve']
        const reserveDuration = results.queryParams['reserveDuration']
        const reservePeople = results.queryParams['reservePeople']

        // Search by geolocation
        if (latitude && longitude) {
          const radius = 100000;

          from(this.refreshRoomsByGeolocation(latitude, longitude, radius))
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(async rooms => {
              this.isFilterGeolocation = true
              this.latitude = parseFloat(latitude)
              this.longitude = parseFloat(longitude)
              this.zoom = 12

              // Reset region
              this.region = null

              // Map bounds
              const softBound = false; // Not enabled for geolocation because the zoom level is not configured
              const hardBound = true; // Force a hard bound because results may be out of bound

              // Filter rooms
              let filteredRooms = rooms;

              // Filter based reservation
              if (reserve && reserveDuration && reservePeople) {
                this.isFilterReservation = true
                this.reserve = reserve
                this.reserveDuration = reserveDuration
                this.reservePeople = reservePeople

                // Filter based on number of people
                filteredRooms = filteredRooms.filter(room => {
                  return room.maxSeats >= reservePeople
                })

                // Filter based on schedule
                let roomIds = filteredRooms.map(room => room._id);
                let date = reserve
                let duration = reserveDuration * 60 * 60 * 1000; // convert hours to milliseconds
                let schedules = await firstValueFrom(this.scheduleService.findByRoomIds(roomIds, date, duration).pipe(takeUntil(this.onDestroy$)))

                filteredRooms = filteredRooms.filter(room => {
                  let schedule = schedules.find(schedule => schedule.room && schedule.room._id === room._id);
                  return schedule && schedule.open
                })
              }

              // Sort by lowest seat count
              filteredRooms.sort((a, b) => a.maxSeats < b.maxSeats ? -1 : 1)

              this.rooms = filteredRooms;

              this.refreshNeighborhood();
              this.refreshMap(softBound, hardBound)

              this.loading = false
            });
          return;
        }

        // Search by region (default)
        this.regionSlug = results.params['regionSlug'];
        this.neighborhoodSlug = results.params['neighborhoodSlug'];
        this.placeSlug = results.params['placeSlug'];

        // Region list
        if (this.regionSlug) {
          this.regionService.regionSlug$.next(this.regionSlug)
        }

        // Using default region slug
        if (this.regionSlug == null) {
          this.regionSlug = await this.regionService.getRegionSlug()
        }

        const regionPromise = this.refreshRegion(this.regionSlug)
        const roomsPromise = this.refreshRooms(this.regionSlug)
        const placePromise = this.placeSlug ? this.refreshPlace(this.placeSlug) : Promise.resolve(null);

        forkJoin([regionPromise, roomsPromise, placePromise])
          .pipe(takeUntil(this.onDestroy$))
          .subscribe(async results => {
            this.region = results[0]
            this.place = results[2]

            // Map bounds
            let softBound = false // Region: false, Neighborhood: true, Place: true
            let hardBound = false // Not needed since it's not a geolocation search

            // Set region center
            this.latitude = this.region.map!.web!.latitude!
            this.longitude = this.region.map!.web!.longitude!
            this.zoom = this.region.map!.web!.zoom!

            // Filter rooms
            let rooms = results[1]
            let filteredRooms = rooms

            // Filter based on neighborhood slug
            if (this.neighborhoodSlug) {
              filteredRooms = filteredRooms.filter(room => {
                let neighborhoodSlug = this.generalService.kebabCase(room.place!.location!.neighborhood!)
                return neighborhoodSlug === this.neighborhoodSlug
              })
            }

            // Filter based on place slug
            if (this.placeSlug) {
              this.place = await this.refreshPlace(this.placeSlug)

              filteredRooms = filteredRooms.filter(room => {
                return room.place!.slug! == this.placeSlug
              })
            }

            // Filter based reservation
            if (reserve && reserveDuration && reservePeople) {
              this.isFilterReservation = true
              this.reserve = reserve
              this.reserveDuration = reserveDuration
              this.reservePeople = reservePeople

              // Filter based on number of people
              filteredRooms = filteredRooms.filter(room => {
                return room.maxSeats >= reservePeople
              })

              // Filter based on schedule
              let roomIds = filteredRooms.map(room => room._id);
              let date = reserve
              let duration = reserveDuration * 60 * 60 * 1000; // convert hours to milliseconds
              let schedules = await firstValueFrom(this.scheduleService.findByRoomIds(roomIds, date, duration).pipe(takeUntil(this.onDestroy$)))

              filteredRooms = filteredRooms.filter(room => {
                let schedule = schedules.find(schedule => schedule.room && schedule.room._id === room._id);
                return schedule && schedule.open
              })
            }

            // No results (zoom into region map)
            if (filteredRooms.length === 0) {
              softBound = false;
            }

            // Partial results (zoom into bounded results)
            if (filteredRooms.length > 0 && filteredRooms.length !== rooms.length) {
              softBound = true;
            }

            // Full result (zoom into region map)
            if (filteredRooms.length > 0 && filteredRooms.length === rooms.length) {
              softBound = false;
            }

            // Sort by lowest seat count
            filteredRooms.sort((a, b) => a.maxSeats < b.maxSeats ? -1 : 1)

            this.rooms = filteredRooms

            this.refreshNeighborhood();
            this.refreshMap(softBound, hardBound)

            this.loading = false
          })
      });
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  async refreshRegion(regionSlug: string) {
    this.isRegionLoading = true;
    return firstValueFrom(this.regionService.getBySlug(regionSlug).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isRegionLoading = false
        this.isRegionLoaded = true
      })))
  }

  async refreshRooms(regionSlug: string) {
    this.isRoomsLoading = true;
    return firstValueFrom(this.roomService.findByRegion(regionSlug).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isRoomsLoading = false
        this.isRoomsLoaded = true
      })))
  }

  async refreshPlace(placeSlug: string) {
    this.isPlaceLoading = true;
    return firstValueFrom(this.placeService.getBySlug(placeSlug).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isPlaceLoading = false
        this.isPlaceLoaded = true
      })))
  }

  async refreshRoomsByGeolocation(latitude: number, longitude: number, radius: number) {
    this.isRoomsLoading = true;
    return firstValueFrom(this.roomService.findByGeolocation(latitude, longitude, radius).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isRoomsLoading = false
        this.isRoomsLoaded = true
      })))
  }

  refreshNeighborhood() {
    // Generate all the neighborhoods based on all the places
    const neighborhoods: any[] = [];

    this.rooms.map(room => room.place!).forEach(place => {
      let isNeighborhoodExist = false;

      // Generic neighborhood ID
      const neighborhoodId = this.generalService.kebabCase(place.location!.regions![0] + ' ' + place!.location!.neighborhood!)
      const neighborhoodName = place!.location!.neighborhood!
      const neighborhoodSlug = this.generalService.kebabCase(place.location!.neighborhood!)

      // Attempt to search for neighborhoods and update the count
      neighborhoods.forEach(neighborhood => {
        if (neighborhoodId === neighborhood._id) {
          isNeighborhoodExist = true;
          neighborhood.count++;
        }
      });

      // Create a generic neighborhood and add it to the neighborhoods
      if (!isNeighborhoodExist) {
        const neighborhood = {
          _id: neighborhoodId,
          name: neighborhoodName,
          slug: neighborhoodSlug,
          count: 1,
        };
        neighborhoods.push(neighborhood);
      }
    });

    this.filterService.neighborhoods$.next(neighborhoods);
  }

  refreshMap(softBound: boolean, hardBound: boolean) {

    // Consolidate multiple rooms for the same place to a single marker
    const marks: MapMark[] = [];

    for (const room of this.rooms) {
      const existingMark = marks.find(mark => {
        let matchLatitude = mark.latitude === room.place!.location!.latitude!
        let matchLongitude = mark.longitude === room.place!.location!.longitude!
        return matchLatitude && matchLongitude
      });

      // Existing
      if (existingMark) {
        existingMark.rooms!.push(room);
      }

      // New
      if (!existingMark) {
        marks.push({
          latitude: room.place!.location!.latitude!,
          longitude: room.place!.location!.longitude!,
          isOpen: room.place!.isOpen!,
          place: room.place!,
          rooms: [room]
        });
      }
    }

    this.mapService.setMapState(this.latitude!, this.longitude!, this.zoom!, marks, softBound, hardBound);
  }

  clearFilters() {
    this.filterService.resetFilter();

    this.isFilterGeolocation = false
    this.isFilterReservation = false

    this.region = null
    this.reserve = null
    this.reserveDuration = null
    this.reservePeople = null
  }

}
